import {combine, createEffect} from 'effector';
import {createGate} from 'effector-react';

import {fetchPagedStaffList} from 'api/profile';
import {
    blockUser,
    boundUserWithParticipant,
    createUser,
    removeUser,
    unblockUser,
    updateUser,
} from 'api/registration';
import {createGridTableModel} from 'core/hooks/grid2.0';
import {rootDomain} from 'core/rootDomain';
import {StaffAccessors, StaffListRawRow, StaffPaginationContent} from 'types/profile';

const domain = rootDomain.createDomain('staffTableDomain');

export const Gate = createGate('StaffTableGate');

export const fetchDataFx = domain.createEffect({
    name: 'fetchDataFx',
    handler: fetchPagedStaffList,
});

export const createUserFx = createEffect({
    name: 'createUserFx',
    handler: createUser,
});

export const updateUserFx = createEffect({
    name: 'updateUserFx',
    handler: updateUser,
});

export const removeUserFx = createEffect({
    name: 'removeUserFx',
    handler: removeUser,
});

export const boundUserWithParticipantFx = createEffect({
    name: 'boundUserWithParticipantFx',
    handler: boundUserWithParticipant,
});

export const blockUserFx = createEffect({
    name: 'blockUserFx',
    handler: blockUser,
});

export const unblockUserFx = createEffect({
    name: 'unblockUserFx',
    handler: unblockUser,
});

export const onChangeUserIdForRemoveConfirmation = domain.createEvent<number | null>();
export const onChangeIdsForBoundConfirmation = domain.createEvent<{
    userId: number | string;
    clientId?: number | string | null;
} | null>();
export const onSetEditUser = domain.createEvent<StaffListRawRow>();

export const {
    filters$,
    filtersChanged,
    filtersReset,
    defaultFilters,
    sortings$,
    sortingsChanged,
    sortingsReset,
    defaultSortings,
    pageNumber$,
    pageNumberChanged,
    pageSize$,
    pageSizeChanged,
    tableStateChanged,
    stateReset,
} = createGridTableModel({
    domain,
    Gate,
    defaultSortings: {
        [StaffAccessors.name]: false,
    },
});

export const idsForRemoveBoundedConfirmation$ = domain.createStore<{
    userId: number | string;
    clientId?: number | string | null;
} | null>(null);

export const userIdForRemoveConfirmation$ = domain.createStore<number | null>(null);
export const editUserData$ = domain.createStore<StaffListRawRow | null>(null);
export const data$ = domain.createStore<StaffPaginationContent<StaffListRawRow> | null>(null);

export const $blockUserModal = domain.createStore<{
    isOpen: boolean;
    userId: number | string;
    isNextStep?: boolean;
    toBlock?: boolean;
} | null>(null);

export const setBlockUserModal = domain.createEvent<{
    isOpen: boolean;
    userId: number | string;
    isNextStep?: boolean;
    toBlock?: boolean;
} | null>();
export const onBlockUser = domain.createEvent<string>('');
export const onUnblockUser = domain.createEvent<string>('');

export const $userStatusChangePending = combine(
    blockUserFx.pending,
    unblockUserFx.pending,
    (blockUserPending, unblockUserPending) => blockUserPending || unblockUserPending,
);

export const staffState$ = combine({
    data: data$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    filters: filters$,
    sortings: sortings$,
    isLoading: fetchDataFx.pending,
});
